/**
 * AC-1598 / AC-1594: CopyWorkflowModal.
 *
 * Renders a modal that lets the user copy an existing workflow into the same
 * project or into a different project they own. The modal calls the AC-1596
 * backend endpoint via `useCopyWorkflow()` and, on success, closes itself and
 * navigates to the newly-created workflow's editor.
 *
 * Behavior contract (locked by
 * `frontend/src/components/workflow/__tests__/CopyWorkflowModal.test.tsx`):
 *
 *   - Name input pre-fills to `"{sourceWorkflow.name} (copy)"`.
 *   - Project picker defaults to `currentProjectId`; options come from
 *     `useProjectStore().projects`.
 *   - Submit always sends `target_project_id` explicitly (even in the
 *     same-project case) — the backend tolerates either, but explicit is
 *     less ambiguous for invalidation downstream.
 *   - On success: call `onClose()` then
 *     `navigate('/projects/{target_project_id}/workflows/{new_orchestration_id}')`.
 *   - On 409 (name collision): show inline error mentioning "already exists";
 *     do NOT close or navigate.
 *   - On 404 / 500 / other: show a generic error; do NOT close or navigate.
 *   - Submit disabled when name is empty (after trim).
 *   - Loading spinner / disabled button while the mutation is in flight.
 *   - Cancel button calls `onClose` without submitting.
 */
import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useModalDismiss } from '../../hooks/useModalDismiss';

import { useProjectStore } from '../../store';
import { useCopyWorkflow } from '../../hooks/queries/useWorkflowMutations';

export interface CopyWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceWorkflow: {
    orchestration_id: number;
    name: string;
    project_id: number;
  };
  currentProjectId: number;
}

/**
 * Extract a user-friendly error message from an axios-style error object.
 * Falls back to a generic message when the backend's detail is not present.
 */
function describeError(err: unknown): { isCollision: boolean; message: string } {
  const errObj = err as
    | {
        response?: { status?: number; data?: { detail?: string } };
        message?: string;
      }
    | undefined;
  const status = errObj?.response?.status;
  const detail = errObj?.response?.data?.detail;

  if (status === 409) {
    return {
      isCollision: true,
      message:
        detail ||
        'A workflow with this name already exists in the target project.',
    };
  }
  return {
    isCollision: false,
    message: detail || 'Failed to copy workflow. Please try again.',
  };
}

const CopyWorkflowModal: React.FC<CopyWorkflowModalProps> = ({
  isOpen,
  onClose,
  sourceWorkflow,
  currentProjectId,
}) => {
  const navigate = useNavigate();
  const { projects } = useProjectStore();
  const copyMutation = useCopyWorkflow();

  const [name, setName] = useState<string>(`${sourceWorkflow.name} (copy)`);
  const [targetProjectId, setTargetProjectId] =
    useState<number>(currentProjectId);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // AC-1602: Escape key + backdrop click dismiss, focus restore on close.
  // Blocked while the mutation is pending so the user does not lose
  // visibility into whether the copy succeeded.
  const { backdropProps } = useModalDismiss({
    isOpen,
    onClose,
    isBlocked: copyMutation.isPending,
  });

  // Defensive reset for parents that keep the modal mounted across open/close cycles.
  useEffect(() => {
    if (isOpen) {
      setName(`${sourceWorkflow.name} (copy)`);
      setTargetProjectId(currentProjectId);
      setErrorMessage(null);
    }
  }, [isOpen, sourceWorkflow, currentProjectId]);

  if (!isOpen) return null;

  const isPending = copyMutation.isPending;
  const isSubmitDisabled = name.trim() === '' || isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    setErrorMessage(null);
    try {
      const result = await copyMutation.mutateAsync({
        workflowId: sourceWorkflow.orchestration_id,
        payload: {
          name: name.trim(),
          target_project_id: targetProjectId,
        },
      });
      onClose();
      navigate(
        `/projects/${targetProjectId}/workflows/${result.orchestration_id}`,
      );
    } catch (err) {
      const { message } = describeError(err);
      setErrorMessage(message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div
        className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0"
        {...backdropProps}
        data-testid="modal-backdrop"
      >
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 dark:bg-black opacity-75"></div>
        </div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white dark:bg-cap-dark rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              aria-label="Close"
              className="bg-white dark:bg-cap-dark rounded-md text-gray-400 hover:text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-warm-white">
                Copy Workflow
              </h3>
              <div className="mt-4">
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  autoComplete="off"
                >
                  <div>
                    <label
                      htmlFor="copy-workflow-name"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="copy-workflow-name"
                      name="copy-workflow-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="copy-workflow-project"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Project
                    </label>
                    <select
                      id="copy-workflow-project"
                      name="copy-workflow-project"
                      value={String(targetProjectId)}
                      onChange={(e) =>
                        setTargetProjectId(Number(e.target.value))
                      }
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      {projects.map((p) => (
                        <option key={p.project_id} value={String(p.project_id)}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {errorMessage && (
                    <div
                      role="alert"
                      className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300"
                    >
                      {errorMessage}
                    </div>
                  )}

                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={isSubmitDisabled}
                      aria-busy={isPending}
                      className="w-full inline-flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isPending && (
                        <Loader2
                          className="w-4 h-4 mr-2 animate-spin"
                          role="status"
                          aria-label="Copying"
                        />
                      )}
                      {isPending ? 'Copying...' : 'Copy'}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-cap-dark text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopyWorkflowModal;
